import { Injectable } from '@angular/core';
import { RequestHeadersType, RequestOptionsType, RequestParamsType } from '@monsido/ng2/external/http/options';
import { CancelerType } from '@monsido/ng2/external/http/http-client.service';
import { ApiClient, SupportApiClient } from '@monsido/ng2/modules/endpoints/api/api-client';
import { Observable } from 'rxjs';
import { contextTokens, RequestAuxiliaryService } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { HttpContextToken } from '@angular/common/http';
import { supportPath } from '@monsido/core/constants/general.constant';

/**
 * @deprecated Use `RequestHeadersType`.
 */
export type ApiConfigType = {
    headers?: RequestHeadersType;
};

function isConfig (headersOrConfig?: RequestHeadersType | ApiConfigType): headersOrConfig is ApiConfigType {
    return typeof headersOrConfig === 'object' &&
        headersOrConfig !== null &&
        'headers' in headersOrConfig;
}

function toHeaders (headersOrConfig?: RequestHeadersType | ApiConfigType): RequestHeadersType {
    if (isConfig(headersOrConfig)) {
        return headersOrConfig.headers || {};
    }

    return headersOrConfig || {};
}

@Injectable({
    providedIn: 'root',
})
export class ApiService {
    constructor (
        private client: ApiClient,
        private supportClient: SupportApiClient,
        private requestAuxiliaryService: RequestAuxiliaryService,
    ) {
        this.client.setHeader('Accept-Version', 'v1');
        this.supportClient.setHeader('Accept-Version', 'v1');
        this.supportClient.setUrlPrefix(supportPath);
    }

    setHeader (name: string, value: string): void {
        this.client.setHeader(name, value);
        this.supportClient.setHeader(name, value);
    }

    removeHeader (name: string): void {
        this.client.removeHeader(name);
        this.supportClient.removeHeader(name);
    }

    setUrlPrefix (prefix: string): void {
        this.client.setUrlPrefix(prefix);
    }

    get<T = unknown> (url: string, params?: RequestParamsType,
        headers?: RequestHeadersType | ApiConfigType, contextToken?: HttpContextToken<unknown>): Promise<T> {
        const context = contextToken ? this.requestAuxiliaryService.setHttpContext(contextToken) : undefined;
        const opts: RequestOptionsType = {};
        if (params !== undefined) {
            opts.params = params;
        }
        const hdrs = toHeaders(headers);
        if (hdrs && Object.keys(hdrs).length > 0) {
            opts.headers = hdrs;
        }
        if (context !== undefined) {
            opts.context = context;
        }
        return this.client.getPromise(url, opts);
    }


    supportClientGet<T = unknown> (url: string, params?: RequestParamsType,
        headers?: RequestHeadersType | ApiConfigType, contextToken?: HttpContextToken<unknown>): Promise<T> {
        const context = contextToken ? this.requestAuxiliaryService.setHttpContext(contextToken) : undefined;
        const opts: RequestOptionsType = {};
        if (params !== undefined) {
            opts.params = params;
        }
        const hdrs = toHeaders(headers);
        if (hdrs && Object.keys(hdrs).length > 0) {
            opts.headers = hdrs;
        }
        if (context !== undefined) {
            opts.context = context;
        }
        return this.supportClient.getPromise(url, opts);
    }

    delete<T = unknown> (url: string, params?: RequestParamsType, contextToken?: HttpContextToken<unknown>): Promise<T> {
        const context = contextToken ? this.requestAuxiliaryService.setHttpContext(contextToken) : undefined;
        const opts: RequestOptionsType = {};
        if (params !== undefined) {
            opts.params = params;
        }
        if (context !== undefined) {
            opts.context = context;
        }
        return this.client.deletePromise(url, opts);
    }

    post<T = unknown> (url: string, body?: unknown, contextToken?: HttpContextToken<unknown>, params?: RequestParamsType): Promise<T> {
        const context = contextToken ? this.requestAuxiliaryService.setHttpContext(contextToken) : undefined;
        const options: RequestOptionsType = {};
        if (context !== undefined) {
            options.context = context;
        }
        if (params !== undefined) {
            options.params = params;
        }
        return this.client.postPromise(url, body, options);
    }

    postAndSkipRedirectOn403<T = unknown> (url: string, body?: unknown, params?: RequestParamsType): Promise<T> {
        const options: RequestOptionsType = {
            context: this.requestAuxiliaryService.setHttpContext(contextTokens.NO_REDIRECT_ON_403),
        };
        if (params !== undefined) {
            options.params = params;
        }
        return this.client.postPromise(url, body, options);
    }

    patch<T = unknown> (url: string, body: unknown, params?: RequestParamsType, contextToken?: HttpContextToken<unknown>): Promise<T> {
        const context = contextToken ? this.requestAuxiliaryService.setHttpContext(contextToken) : undefined;
        const options: RequestOptionsType = {};
        if (context !== undefined) {
            options.context = context;
        }
        if (params !== undefined) {
            options.params = params;
        }
        return this.client.patchPromise(url, body, options);
    }

    patchAndSkipRedirectOn403<T = unknown> (url: string, body: unknown, params?: RequestParamsType): Promise<T> {
        const options: RequestOptionsType = {
            context: this.requestAuxiliaryService.setHttpContext(contextTokens.NO_REDIRECT_ON_403),
        };
        if (params !== undefined) {
            options.params = params;
        }
        return this.client.patchPromise(url, body, options);
    }

    getObservable<T = unknown> (
        url: string,
        params?: RequestParamsType,
        headers?: RequestHeadersType,
        canceler$?: CancelerType,
    ): Observable<T> {
        const opts: RequestOptionsType = {};
        if (params !== undefined) {
            opts.params = params;
        }
        if (headers && Object.keys(headers).length > 0) {
            opts.headers = headers;
        }
        return this.client.getObservable(url, opts, canceler$);
    }

    postObservable<T = unknown> (
        url: string,
        body?: unknown,
        canceler$?: CancelerType,
    ): Observable<T> {
        return this.client.postObservable(url, body, {}, canceler$);
    }

    patchObservable<T = unknown> (
        url: string,
        body: unknown,
        canceler$?: CancelerType,
    ): Observable<T> {
        return this.client.patchObservable(url, body, {}, canceler$);
    }

    deleteObservable<T = unknown> (
        url: string,
        canceler$?: CancelerType,
    ): Observable<T> {
        return this.client.deleteObservable(url, {}, canceler$);
    }

}
